import { useApolloClient } from '@apollo/client';
import { UploadFile } from 'antd/lib/upload/interface';
import imageCompression from 'browser-image-compression';
import { INPUT_TYPE } from 'components/common/Input/types';
import { BYTES_PER_MB } from 'constants/file';
import { MAX_AVATAR_DIMENSION, MAX_AVATAR_SIZE_MB, MAX_AVATAR_THUMBNAIL_DIMENSION } from 'constants/image';
import { ERRORS } from 'constants/notify';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import { get, isString } from 'lodash';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import FileAction from 'redux/file/file.actions';
import { GET_MANY_SIGNED_URLS } from 'services/graphql/queries/signedUrls.query';
import { GeneralFunction } from 'types/common';
import { ISignedUrls } from 'types/signedUrls';
import { defaultNotification } from 'utils';

import { InputProps } from './types';
import UpdateProfileFormTemplate from './UpdateProfileFormTemplate';

const formFields: InputProps[] = [{ name: 'newAvatar', type: INPUT_TYPE.AVATAR }];

const AvatarForm: React.FC<{
  closeModal: () => void;
  isCompanyAvatar?: boolean;
  callbackOnUploadSuccess?: GeneralFunction;
}> = ({ closeModal, isCompanyAvatar, callbackOnUploadSuccess }) => {
  const dispatch = useDispatch();
  const { id: userId, refetchCurrentLoggedInUser, company } = useCurrentLoggedInUser();
  const [loading, setLoading] = useState<boolean>(false);
  const apolloClient = useApolloClient();

  const convertFiles = (files: Array<UploadFile>) =>
    files.map((f) => ({
      name: f.name,
      type: f.type,
      userId,
      isAvatar: true,
      companyId: isCompanyAvatar ? company?.id : undefined,
    }));

  const handleUploadError = (error?: string) => {
    setLoading(false);
    defaultNotification({ failure: isString(error) ? error : ERRORS.DEFAULT_MUTATION_ERROR });
  };

  const handleUploadSuccess = () => {
    refetchCurrentLoggedInUser();
    setLoading(false);
    closeModal();
    defaultNotification({ success: 'Upload avatar successfully!' });
    // onUploadFileSuccess({
    //   pipeFrom,
    //   thumbnailPipeFrom,
    // });
    callbackOnUploadSuccess?.();
  };

  // const handleUpdateUserInfo = async (fileData: Pick<IFileType, 'pipeFrom' | 'thumbnailPipeFrom'>) => {
  //   let updateSuccess = false;
  //   try {
  //     if (!isCompanyAvatar)
  //       dispatch(
  //         UserAction.fetchUserSuccess({
  //           avatar: {
  //             pipeFrom: fileData.pipeFrom,
  //             thumbnailPipeFrom: fileData.thumbnailPipeFrom,
  //           },
  //         }),
  //       );
  //     updateSuccess = true;
  //   } catch (error) {
  //     handleUploadError();
  //   }

  //   return updateSuccess;
  // };

  // const onUploadFileSuccess = async ({
  //   pipeFrom,
  //   thumbnailPipeFrom,
  // }: Pick<IFileType, 'pipeFrom' | 'thumbnailPipeFrom'>) => {
  //   try {
  //     const updateUserInfo = await handleUpdateUserInfo({ pipeFrom, thumbnailPipeFrom });

  //     if (updateUserInfo) {
  //       setTimeout(() => {
  //         notification.success({
  //           message: 'Update avatar successfully!',
  //           key: 'updateProfile',
  //           duration: 2,
  //         });
  //         setLoading(false);
  //         closeModal();
  //       }, 1000);
  //     }
  //   } catch (error) {
  //     handleUploadError();
  //   }
  // };

  const uploadFileFunction = async (filesData: ISignedUrls[], files: UploadFile[]) => {
    const fileTypes: string[] = [];
    const urls: string[] = [];
    const thumbnailUrls: string[] = [];
    const fileOrigins: any[] = [];
    const thumbnailOrigins: File[] = [];
    // const pipeFrom = '';
    // const thumbnailPipeFrom = '';

    try {
      for (let i = 0; i < filesData.length; i += 1) {
        const fData = filesData[i];
        // pipeFrom = fData.pipeFrom;
        // thumbnailPipeFrom = fData.thumbnailPipeFrom;
        urls.push(fData.urls[0]);
        thumbnailUrls.push(fData.thumbnailUrl);
        fileTypes.push(files[i].type);

        const file = files[i];

        if (file.originFileObj && file.size / BYTES_PER_MB <= MAX_AVATAR_SIZE_MB) {
          // eslint-disable-next-line no-await-in-loop
          const thumbnailFile = await imageCompression(file.originFileObj as File, {
            maxSizeMB: MAX_AVATAR_SIZE_MB,
            maxWidthOrHeight: MAX_AVATAR_THUMBNAIL_DIMENSION,
            fileType: 'image/jpeg',
          });

          fileOrigins.push(file.originFileObj);
          thumbnailOrigins.push(thumbnailFile);
        } else if (file.originFileObj) {
          // file size larger MAX_AVATAR_SIZE_MB
          // eslint-disable-next-line no-await-in-loop
          const convertFileOrigin = await imageCompression(file.originFileObj as File, {
            maxSizeMB: MAX_AVATAR_SIZE_MB,
            maxWidthOrHeight: MAX_AVATAR_DIMENSION,
          });
          fileOrigins.push(convertFileOrigin);

          // Thumbnail
          // eslint-disable-next-line no-await-in-loop
          const thumbnailFile = await imageCompression(file.originFileObj as File, {
            maxSizeMB: MAX_AVATAR_SIZE_MB,
            maxWidthOrHeight: MAX_AVATAR_THUMBNAIL_DIMENSION,
            fileType: 'image/jpeg',
          });

          thumbnailOrigins.push(thumbnailFile);
        }
      }

      dispatch(
        FileAction.uploadFileStart(
          {
            files: fileOrigins,
            thumbnails: thumbnailOrigins,
            thumbnailUrls,
            urls,
            fileTypes,
            userId,
          },
          {
            callbackOnSuccess: () => {
              handleUploadSuccess();
            },
            callbackOnFailure: (err) => {
              handleUploadError(err);
            },
          },
        ),
      );
    } catch (error) {
      handleUploadError();
    }
  };

  // get signed urls from s3
  const getSignedUrlsFunction = async (files: Array<UploadFile>) => {
    try {
      const convertedFiles = convertFiles(files);
      return apolloClient.query({
        query: GET_MANY_SIGNED_URLS,
        variables: { fileList: convertedFiles },
        fetchPolicy: 'network-only',
      });
    } catch (err) {
      handleUploadError();
    }
    return null;
  };

  const onSubmit = async (v: any) => {
    setLoading(true);

    try {
      const files = get(v, 'newAvatar', []);
      const urlResponse = await getSignedUrlsFunction(files);
      const urlsData = get(urlResponse, 'data.getManySignedUrls');
      uploadFileFunction(urlsData, files);
    } catch (err) {
      handleUploadError();
    }
  };

  return (
    <div className='gx-avatar-upload-form'>
      <UpdateProfileFormTemplate
        loading={loading}
        name='avatarForm'
        initialFields={formFields}
        onSubmit={onSubmit}
        onCancel={closeModal}
        displaySaveOnFooter
      />
    </div>
  );
};

export default React.memo(AvatarForm);
